import { appClient } from '@/lib/appwrite/appClient';
import {
  APP_COLLECTIONS,
  AppBudget,
  BudgetPeriod,
  Status,
  TransactionType,
} from '@polybank/shared';
import { ID, Query } from 'appwrite';
import { logger } from '@/utils/logger';

/**
 * Service for managing budgets in Appwrite
 */
export class AppwriteBudgetService {
  /**
   * Create a new budget with proper document-level permissions
   */
  async createBudget(budgetData: Omit<AppBudget, '$id'>): Promise<AppBudget> {
    try {
      // Get current user to set proper document permissions
      // const currentUser = await appClient.account.get();

      // // Create document permissions for the current user
      // const permissions = [
      //   Permission.read(Role.user(currentUser.$id)),
      //   Permission.update(Role.user(currentUser.$id)),
      //   Permission.delete(Role.user(currentUser.$id)),
      // ];

      const budget = await appClient.tables.typed.createRow(
        APP_COLLECTIONS.BUDGETS,
        ID.unique(),
        {
          categories: budgetData.categories?.map((cat) => cat.$id!) || [],
          amount: budgetData.amount,
          period: budgetData.period || BudgetPeriod.MONTHLY,
          start_date: budgetData.start_date,
          end_date: budgetData.end_date,
          currency: budgetData.currency,
        }
        // permissions
      );

      return budget;
    } catch (error) {
      logger.error('Failed to create budget:', error);
      throw error;
    }
  }

  /**
   * Update an existing budget
   */
  async updateBudget(
    budgetId: string,
    updates: Partial<Omit<AppBudget, '$id'>>
  ): Promise<AppBudget> {
    try {
      const updatedBudget = await appClient.tables.typed.updateRow(
        APP_COLLECTIONS.BUDGETS,
        budgetId,
        updates
      );

      return updatedBudget;
    } catch (error) {
      logger.error('Failed to update budget:', error);
      throw error;
    }
  }

  /**
   * Delete a budget
   */
  async deleteBudget(budgetId: string): Promise<void> {
    try {
      await appClient.tables.typed.deleteRow(APP_COLLECTIONS.BUDGETS, budgetId);
    } catch (error) {
      logger.error('Failed to delete budget:', error);
      throw error;
    }
  }

  /**
   * Get all budgets for the current user
   * Uses document-level permissions - only returns budgets the user has access to
   */
  async getUserBudgets(): Promise<AppBudget[]> {
    try {
      const budgetsResponse = await appClient.tables.typed.listRows(
        APP_COLLECTIONS.BUDGETS,
        [
          Query.orderDesc('start_date'),
          Query.limit(100),
          // Note: No user_id filter needed - Appwrite's document permissions
          // automatically filter to only show documents the user can read
        ]
      );

      return budgetsResponse.rows;
    } catch (error) {
      logger.error('Failed to get user budgets:', error);
      throw error;
    }
  }

  /**
   * Get a single budget by ID
   */
  async getBudget(budgetId: string): Promise<AppBudget | null> {
    try {
      const budget = await appClient.tables.typed.getRow(
        APP_COLLECTIONS.BUDGETS,
        budgetId
      );

      return budget as AppBudget;
    } catch (error) {
      logger.error('Failed to get budget:', error);
      return null;
    }
  }

  /**
   * Calculate spent amount for a budget
   * Queries transactions that match the budget's categories, date range, and currency
   */
  async calculateBudgetSpent(budget: AppBudget): Promise<number> {
    try {
      // If budget has no categories, return 0
      if (!budget.categories || budget.categories.length === 0) {
        return 0;
      }

      // Extract category IDs
      const categoryIds = budget.categories.map((cat) =>
        typeof cat === 'string' ? cat : cat.$id
      );

      // Query transactions that match the budget criteria
      const transactionsResponse = await appClient.tables.typed.listRows(
        APP_COLLECTIONS.TRANSACTIONS,
        [
          // Match any of the budget's categories
          Query.equal('category_id', categoryIds),
          // Within the budget period
          Query.greaterThanEqual('transaction_date', budget.start_date),
          Query.lessThanEqual('transaction_date', budget.end_date),
          // Match currency
          Query.equal('currency', budget.currency),
          // Only DEBIT transactions (expenses)
          Query.equal('transaction_type', TransactionType.DEBIT),
          // Only BOOKED transactions (not pending)
          Query.equal('status', Status.BOOKED),
          // We only need the amounts
          Query.select(['amount']),
          // Get all matching transactions
          Query.limit(5000),
        ]
      );

      // Sum up all transaction amounts
      const totalSpent = transactionsResponse.rows.reduce(
        (sum, transaction) => sum + Math.abs(transaction.amount || 0),
        0
      );

      return totalSpent;
    } catch (error) {
      logger.error('Failed to calculate budget spent:', error);
      return 0;
    }
  }

  /**
   * Get current month's budget for specific categories
   * Helper method to find if a budget exists for the current period
   * Uses document-level permissions
   */
  async getCurrentMonthBudget(
    _categoryIds: string[]
  ): Promise<AppBudget | null> {
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const budgetsResponse = await appClient.tables.typed.listRows(
        APP_COLLECTIONS.BUDGETS,
        [
          Query.greaterThanEqual('start_date', startOfMonth.toISOString()),
          Query.lessThanEqual('end_date', endOfMonth.toISOString()),
          Query.limit(1),
          // Document permissions automatically filter to user's budgets
        ]
      );

      return budgetsResponse.rows[0] || null;
    } catch (error) {
      logger.error('Failed to get current month budget:', error);
      return null;
    }
  }
}

// Export singleton instance
export const appwriteBudgetService = new AppwriteBudgetService();
